/**
 * CmcFilter - Mana value (CMC) filter component
 *
 * Renders circular chips for filtering cards by converted mana cost.
 * Supports values 0-5 and 6+ (for cards costing 6 or more).
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';

/** CMC filter values */
export type CmcFilterValue = 0 | 1 | 2 | 3 | 4 | 5 | '6+';

export const CMC_VALUES: CmcFilterValue[] = [0, 1, 2, 3, 4, 5, '6+'];

export interface CmcFilterProps {
  /** Currently selected CMC values */
  values: CmcFilterValue[];
  /** Callback when a CMC value is toggled */
  onToggle: (value: CmcFilterValue) => void;
}

export default function CmcFilter({ values, onToggle }: CmcFilterProps): ReactElement {
  return (
    <div className="flex items-center gap-1">
      {CMC_VALUES.map((cmc) => (
        <CmcChip key={String(cmc)} value={cmc} isActive={values.includes(cmc)} onClick={() => onToggle(cmc)} />
      ))}
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

interface CmcChipProps {
  value: CmcFilterValue;
  isActive: boolean;
  onClick: () => void;
}

function CmcChip({ value, isActive, onClick }: CmcChipProps): ReactElement {
  const label = typeof value === 'string' ? value : String(value);

  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'flex h-6 min-w-6 items-center justify-center rounded-full text-xs font-medium transition-all',
        'focus-visible:ring-aether focus-visible:ring-offset-void focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none',
        isActive ? 'bg-aether text-white' : 'bg-slate text-silver hover:bg-storm hover:text-pearl',
      )}
      aria-pressed={isActive}
      aria-label={`Filter by CMC ${label}`}
    >
      {label}
    </button>
  );
}
